"use client"

import { useMemo } from "react"
import { motion } from "framer-motion"
import {
    Users,
    Trophy,
    TrendingUp,
    GraduationCap,
    Crown,
    Award,
    Star,
    Mail,
    ChevronRight,
    Calendar,
    Clock,
    Target,
    Sparkles,
} from "@/lib/icons"
import { formatNumber, formatPercentage } from "@/lib/utils"

// Excluir admins del ranking — solo CAPs reales
const ADMIN_FILTER = new Set([
    "admin@tepeyac.mx",
    "ramiro@tepeyact.com",
    "yair@tepeyac.mx",
    "yair@tepeyac.edu.mx",
])

// Catálogo de CAPs (mismo que el modal — coherencia visual)
const CAP_INFO = {
    gaby: {
        territory: "Niveles bajos",
        label: "Maternal · Kinder · Pre-First",
        accent: "#d97706",
        accentSoft: "#fef3c7",
        accentTint: "rgba(217, 119, 6, 0.06)",
    },
    lulu: {
        territory: "Niveles altos",
        label: "Primaria · Secundaria · Prepa",
        accent: "#0f766e",
        accentSoft: "#ccfbf1",
        accentTint: "rgba(15, 118, 110, 0.06)",
    },
}

const DEFAULT_CAP = {
    territory: "Equipo",
    label: "General",
    accent: "#7a1b33",
    accentSoft: "#fce7f3",
    accentTint: "rgba(122, 27, 51, 0.06)",
}

const MEDAL = [
    { icon: Crown, color: "#f59e0b", label: "1°" },
    { icon: Award, color: "#94a3b8", label: "2°" },
    { icon: Star, color: "#f97316", label: "3°" },
]

function getCapInfo(advisor) {
    const email = String(advisor.email || "").toLowerCase()
    const nombre = String(advisor.nombre || "").toLowerCase()
    if (email.includes("gaby") || nombre.includes("gaby")) return CAP_INFO.gaby
    if (email.includes("lulu") || nombre.includes("lulu")) return CAP_INFO.lulu
    return DEFAULT_CAP
}

// Score (mismo concepto que el modal — A/B/C/D)
function getScore(conversion, total) {
    if (total < 3) return { letter: "—", label: "Datos insuf.", color: "#94a3b8", bg: "rgba(148,163,184,0.1)" }
    if (conversion >= 30) return { letter: "A", label: "Excelente", color: "#10b981", bg: "rgba(16,185,129,0.12)" }
    if (conversion >= 20) return { letter: "B", label: "Muy bien", color: "#0ea5e9", bg: "rgba(14,165,233,0.12)" }
    if (conversion >= 10) return { letter: "C", label: "Aceptable", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" }
    if (conversion > 0) return { letter: "D", label: "Por mejorar", color: "#f97316", bg: "rgba(249,115,22,0.12)" }
    return { letter: "—", label: "Sin conversión", color: "#94a3b8", bg: "rgba(148,163,184,0.1)" }
}

// Mini-insight para cada CAP (lo más importante para supervisor de un vistazo)
function getInsight(advisor, conversion, totalLeads) {
    const inscritos = advisor.inscritos || 0
    if (totalLeads === 0) return { tone: "info", icon: Clock, label: "Sin leads asignados" }
    if (conversion >= 30) return { tone: "good", icon: Crown, label: "Top performer" }
    if (totalLeads >= 5 && conversion === 0) return { tone: "bad", icon: TrendingUp, label: "Sin inscritos" }
    if (conversion >= 15) return { tone: "good", icon: Sparkles, label: "Buen ritmo" }
    if (totalLeads >= 3) return { tone: "warn", icon: Target, label: `${inscritos}/${totalLeads} convertidos` }
    return { tone: "info", icon: Clock, label: "En arranque" }
}

const INSIGHT_TONE = {
    good: { color: "#10b981", bg: "bg-emerald-50/70 dark:bg-emerald-950/20" },
    warn: { color: "#f59e0b", bg: "bg-amber-50/70 dark:bg-amber-950/20" },
    bad: { color: "#ef4444", bg: "bg-rose-50/70 dark:bg-rose-950/20" },
    info: { color: "#64748b", bg: "bg-slate-50/70 dark:bg-slate-900/40" },
}

export function TeamView({ rankedAdvisors = [], onSelectAdvisor }) {
    const caps = useMemo(
        () => rankedAdvisors.filter((a) => !ADMIN_FILTER.has(String(a.email || "").toLowerCase().trim())),
        [rankedAdvisors],
    )

    const teamKpis = useMemo(() => {
        const totalLeads = caps.reduce((s, a) => s + (a.totalLeads || 0), 0)
        const totalInscritos = caps.reduce((s, a) => s + (a.inscritos || 0), 0)
        const totalCitas = caps.reduce((s, a) => s + (a.citas || 0), 0)
        const conversion = totalLeads > 0 ? (totalInscritos / totalLeads) * 100 : 0
        return { totalLeads, totalInscritos, totalCitas, conversion }
    }, [caps])

    if (caps.length === 0) {
        return (
            <div className="rounded-2xl border border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900 shadow-sm p-12 text-center">
                <Users className="h-10 w-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-sm font-semibold text-slate-500">Sin asesoras en el equipo</p>
                <p className="text-xs text-slate-400 mt-1">No hay CAPs registradas para este mes.</p>
            </div>
        )
    }

    return (
        <div className="space-y-4">
            {/* ── Strip de KPIs del equipo (más compacto, editorial) ── */}
            <div className="rounded-2xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900/60 overflow-hidden">
                <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-100 dark:divide-slate-800">
                    {[
                        { label: "Equipo activo", value: caps.length, icon: Users, color: "#7a1b33", helper: caps.length === 1 ? "CAP activa" : "CAPs activas" },
                        { label: "Total leads", value: formatNumber(teamKpis.totalLeads), icon: TrendingUp, color: "#0ea5e9", helper: "del mes" },
                        { label: "Citas", value: formatNumber(teamKpis.totalCitas), icon: Calendar, color: "#8b5cf6", helper: "agendadas" },
                        { label: "Conversión", value: formatPercentage(teamKpis.conversion), icon: Trophy, color: "#10b981", helper: `${teamKpis.totalInscritos} inscritos` },
                    ].map((k) => {
                        const Icon = k.icon
                        return (
                            <div key={k.label} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50/50 dark:hover:bg-slate-900/40 transition-colors">
                                <div className="flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: `${k.color}15` }}>
                                    <Icon className="h-4 w-4" style={{ color: k.color }} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[9.5px] uppercase tracking-[0.18em] font-extrabold text-slate-500 dark:text-slate-400">
                                        {k.label}
                                    </p>
                                    <p className="text-lg font-extrabold tabular-nums text-slate-900 dark:text-white leading-none mt-0.5">
                                        {k.value}
                                    </p>
                                    <p className="text-[9.5px] text-slate-400 dark:text-slate-500 truncate mt-0.5">
                                        {k.helper}
                                    </p>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* ── Cards premium de cada CAP ── */}
            <div className={`grid gap-4 ${caps.length === 1 ? "grid-cols-1 max-w-md" : "grid-cols-1 md:grid-cols-2"}`}>
                {caps.map((advisor, idx) => {
                    const totalLeads = advisor.totalLeads || 0
                    const inscritos = advisor.inscritos || 0
                    const citas = advisor.citas || 0
                    const conversion = totalLeads > 0 ? (inscritos / totalLeads) * 100 : 0
                    const firstName = (advisor.nombre || "Asesor").split(" ")[0]
                    const initials = (advisor.nombre || "?").split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase()
                    const cap = getCapInfo(advisor)
                    const score = getScore(conversion, totalLeads)
                    const medal = MEDAL[idx] || null
                    const MedalIcon = medal?.icon
                    const insight = getInsight(advisor, conversion, totalLeads)
                    const InsightIcon = insight.icon
                    const insightTone = INSIGHT_TONE[insight.tone]

                    return (
                        <motion.button
                            key={advisor.email || idx}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: idx * 0.08 }}
                            onClick={() => onSelectAdvisor?.(advisor)}
                            className="group relative overflow-hidden rounded-3xl border border-slate-200/60 dark:border-slate-800 bg-white dark:bg-slate-900/60 text-left shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                        >
                            {/* HERO BACKGROUND con gradient del color del CAP */}
                            <div
                                className="absolute inset-x-0 top-0 h-32 opacity-90"
                                style={{
                                    background: `linear-gradient(135deg, ${cap.accent} 0%, ${cap.accent}cc 60%, ${cap.accent}66 100%)`,
                                }}
                            />
                            {/* Decorative orbs */}
                            <div className="pointer-events-none absolute top-0 right-0 h-40 w-40 rounded-full bg-white/15 blur-3xl" />
                            <div className="pointer-events-none absolute -top-8 -left-8 h-32 w-32 rounded-full bg-white/10 blur-2xl" />

                            <div className="relative">
                                {/* HERO TOP */}
                                <div className="px-5 pt-5 pb-4 flex items-start gap-4">
                                    {/* Avatar grande */}
                                    <div className="relative flex-shrink-0">
                                        <div className="absolute inset-0 rounded-2xl bg-white/40 blur-xl" />
                                        <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-white/25 backdrop-blur-sm text-white text-xl font-extrabold shadow-2xl ring-2 ring-white/40">
                                            {initials}
                                        </div>
                                        {medal && MedalIcon && (
                                            <div
                                                className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-white dark:bg-slate-900 ring-2 ring-white dark:ring-slate-900 flex items-center justify-center shadow-lg"
                                                title={`${medal.label} lugar`}
                                            >
                                                <MedalIcon className="h-4 w-4" style={{ color: medal.color }} />
                                            </div>
                                        )}
                                    </div>

                                    {/* Identidad sobre el hero */}
                                    <div className="flex-1 min-w-0 pt-0.5">
                                        <h3 className="text-xl font-extrabold text-white truncate leading-none drop-shadow-sm">
                                            {firstName}
                                        </h3>
                                        <span className="inline-block mt-1.5 text-[9.5px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/25 backdrop-blur-sm text-white ring-1 ring-white/30">
                                            {cap.territory}
                                        </span>
                                        <p className="text-[10.5px] text-white/85 truncate flex items-center gap-1.5 mt-1.5 font-medium">
                                            <GraduationCap className="h-3 w-3" />
                                            {cap.label}
                                        </p>
                                    </div>

                                    {/* Score letter */}
                                    <div
                                        className="flex flex-col items-center justify-center h-14 w-14 rounded-2xl flex-shrink-0 bg-white/95 dark:bg-white shadow-lg ring-2 ring-white/40"
                                        title={score.label}
                                    >
                                        <span className="text-2xl font-black leading-none tabular-nums" style={{ color: score.color }}>
                                            {score.letter}
                                        </span>
                                        <span className="text-[7.5px] font-bold uppercase tracking-wider mt-0.5" style={{ color: score.color }}>
                                            Score
                                        </span>
                                    </div>
                                </div>

                                {/* CARD BODY (blanco, separado del hero) */}
                                <div className="bg-white dark:bg-slate-950 rounded-t-2xl -mt-3 relative pt-4 pb-4 px-5 border-t border-slate-100 dark:border-slate-800/60">
                                    {/* Email en stripe sutil */}
                                    {advisor.email && (
                                        <div className="flex items-center gap-1.5 text-[10px] text-slate-400 dark:text-slate-500 mb-3.5">
                                            <Mail className="h-2.5 w-2.5" />
                                            <span className="truncate">{advisor.email}</span>
                                        </div>
                                    )}

                                    {/* 3 stats horizontales */}
                                    <div className="grid grid-cols-3 gap-2 mb-3">
                                        <div className="rounded-xl border border-sky-100 dark:border-sky-900/40 bg-sky-50/60 dark:bg-sky-950/20 px-3 py-2 text-center">
                                            <p className="text-xl font-extrabold tabular-nums leading-none text-sky-700 dark:text-sky-300">
                                                {totalLeads}
                                            </p>
                                            <p className="text-[9px] uppercase tracking-wider font-extrabold mt-1.5 text-sky-600/80 dark:text-sky-400/80">
                                                Leads
                                            </p>
                                        </div>
                                        <div className="rounded-xl border border-emerald-100 dark:border-emerald-900/40 bg-emerald-50/60 dark:bg-emerald-950/20 px-3 py-2 text-center">
                                            <p className="text-xl font-extrabold tabular-nums leading-none text-emerald-700 dark:text-emerald-300">
                                                {inscritos}
                                            </p>
                                            <p className="text-[9px] uppercase tracking-wider font-extrabold mt-1.5 text-emerald-600/80 dark:text-emerald-400/80">
                                                Inscritos
                                            </p>
                                        </div>
                                        <div className="rounded-xl border px-3 py-2 text-center" style={{ borderColor: `${cap.accent}30`, backgroundColor: cap.accentTint }}>
                                            <p className="text-xl font-extrabold tabular-nums leading-none" style={{ color: cap.accent }}>
                                                {Math.round(conversion)}<span className="text-sm opacity-60">%</span>
                                            </p>
                                            <p className="text-[9px] uppercase tracking-wider font-extrabold mt-1.5" style={{ color: cap.accent }}>
                                                Conv.
                                            </p>
                                        </div>
                                    </div>

                                    {/* Status-line + CTA */}
                                    <div className="flex items-center justify-between gap-2">
                                        <div className={`flex-1 min-w-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ${insightTone.bg}`}>
                                            <InsightIcon className="h-3 w-3 flex-shrink-0" style={{ color: insightTone.color }} />
                                            <span className="text-[10.5px] font-bold truncate" style={{ color: insightTone.color }}>
                                                {insight.label}
                                            </span>
                                        </div>
                                        <div className="inline-flex items-center gap-1 text-slate-400 dark:text-slate-500 group-hover:text-slate-700 dark:group-hover:text-slate-200 transition-colors flex-shrink-0">
                                            <span className="text-[9.5px] font-bold uppercase tracking-wider whitespace-nowrap">Ver detalle</span>
                                            <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.button>
                    )
                })}
            </div>
        </div>
    )
}
